import { useCallback, useEffect, useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getActiveSlides } from '../config/slides'
import type { WrappedData } from '../types/wrapped'
import {
  wrappedStoryStateReducer,
  WrappedStoryActions,
  INITIAL_WRAPPED_STORY_STATE,
  WrappedStoryState,
  WrappedStoryDispatchAction,
} from 'contexts/Wrapped'
import { getMyWrapped, getSharedWrapped } from 'api/wrapped/wrapped'
import { getWindowMockData } from '../devMocks'

interface UseWrappedDataOptions {
  uuid?: string
}

interface UseWrappedDataResult {
  isLoading: boolean
  hasError: boolean
  error: Error | null
  data: WrappedData | undefined
  state: WrappedStoryState
  dispatch: React.Dispatch<WrappedStoryDispatchAction>
  handleRetry: () => void
}

function useWrappedData({ uuid }: UseWrappedDataOptions): UseWrappedDataResult {
  const [state, dispatch] = useReducer(
    wrappedStoryStateReducer,
    INITIAL_WRAPPED_STORY_STATE,
  )

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['wrapped', uuid ?? 'me'],
    queryFn: async() => {
      // Check for mock data first (development/QA only)
      const mockData = getWindowMockData()
      if (mockData) return mockData

      return uuid ? getSharedWrapped(uuid) : getMyWrapped()
    },
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (data) {
      const slides = getActiveSlides(data)
      dispatch({
        type: WrappedStoryActions.setData,
        data,
        slides,
      })
    }
  }, [data])

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  return {
    isLoading,
    hasError: isError,
    error: error as Error | null,
    data,
    state,
    dispatch,
    handleRetry,
  }
}

export default useWrappedData
